import React, { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isNil } from "lodash";
import { Tooltip } from "@evergis/ui";

import {
  ProgressAlias,
  ProgressBar,
  ProgressBarContainer,
  ProgressBarWrapper,
  ProgressContainerWrapper,
  ProgressContent,
  ProgressIcon,
  ProgressInnerValue,
  ProgressTooltipValue,
  ProgressTooltipValueOf,
  ProgressUnits,
  ProgressValue,
} from "./styled";
import { formatAttributeValue } from "../../../utils/formatAttributeValue";
import { ContainerProps } from "../types";

export const ProgressContainer: FC<ContainerProps> = memo(
  ({ elementConfig, attributes, maxValue, index, renderElement }) => {
    const { t } = useTranslation("featureCard");
    const { children, options, style } = elementConfig || {};
    const {
      bgColor,
      innerTemplateStyle,
      maxValue: optionMaxValue,
      hideTitle,
      innerValue,
      colors,
      colorAttribute,
    } = options;
    const valueElement = children?.find(({ id }) => id === "value");
    const valueAttribute = valueElement ? attributes?.find(({ name }) => name === valueElement.attributeName) : null;
    const { type: valueType, stringFormat } = valueAttribute || {};
    const value = !isNil(valueAttribute?.value) ? +valueAttribute.value : +valueElement.value || 0;
    const currentMaxValue = (maxValue || optionMaxValue) as number;
    const width = `${value > 0 ? Math.min(100, (value / currentMaxValue) * 100).toFixed(2) : 0}%`;

    const renderAlias = useMemo(() => renderElement({ id: "alias" }), [renderElement]);

    const renderValue = useMemo(() => renderElement({ id: "value" }), [renderElement]);

    const renderIcon = useMemo(() => renderElement({ id: "icon", wrap: false }), [renderElement]);

    const renderTooltip = useMemo(
      () => (
        <>
          <div>{renderAlias}</div>
          <ProgressTooltipValue>
            <div>{renderValue}</div>
            <ProgressTooltipValueOf>{t("of")}</ProgressTooltipValueOf>
            <div>{formatAttributeValue(valueType, currentMaxValue, stringFormat)}</div>
          </ProgressTooltipValue>
        </>
      ),
      [currentMaxValue, renderAlias, renderValue, stringFormat, t, valueType],
    );

    const color = useMemo(
      () =>
        colorAttribute
          ? (attributes.find(({ name }) => name === colorAttribute)?.value as string)
          : colors?.[index] || bgColor,
      [attributes, bgColor, colorAttribute, colors, index],
    );

    return (
      <Tooltip content={renderTooltip} placement="top" arrow>
        {ref => (
          <ProgressContainerWrapper ref={ref} style={innerTemplateStyle || style}>
            {renderIcon && <ProgressIcon>{renderIcon}</ProgressIcon>}
            <ProgressContent>
              {!hideTitle && (
                <ProgressAlias>
                  <div>{renderAlias}</div>
                  <ProgressValue>
                    {renderValue}
                    <ProgressUnits>{renderElement({ id: "units" })}</ProgressUnits>
                  </ProgressValue>
                </ProgressAlias>
              )}
              <ProgressBarContainer innerValue={innerValue}>
                <ProgressBarWrapper>
                  <ProgressBar $width={width} $color={color} />
                </ProgressBarWrapper>
                {!!(hideTitle || innerValue) && <ProgressInnerValue>{renderValue}</ProgressInnerValue>}
              </ProgressBarContainer>
            </ProgressContent>
          </ProgressContainerWrapper>
        )}
      </Tooltip>
    );
  },
);
